import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, concatMap, from, map, Observable, of, take, tap, throwError } from 'rxjs';
import { FirestoreService } from '../../services/firestore.service';
import { OrganizationData } from '../../shared/models/organization-data.model';
import { ensureVersion, toDate } from '../../shared/utils/version.utils';
import { VersionRow } from './models/version-row.model';

export interface BulkDeployRowResult {
    id: string;
    newVersion: string;
    outcome: 'success' | 'error';
}

@Injectable()
export class AppVersionsService {
    constructor(
        private readonly firestoreService: FirestoreService,
        private readonly snackBar: MatSnackBar
    ) {}

    public loadOrganizations(getExistingRows: () => VersionRow[]): Observable<VersionRow[]> {
        return this.firestoreService.getOrganizations().pipe(
            map((orgs) => {
                const existingById = new Map(getExistingRows().map((row) => [row.id, row]));
                return orgs
                    .filter((org) => !!org.id)
                    .map((org) => {
                        const newRow = this.toVersionRow(org);
                        const existing = existingById.get(newRow.id);
                        if (!existing) return newRow;
                        return {
                            ...newRow,
                            editVersion: existing.editVersion,
                            isEditVersionValid: existing.isEditVersionValid,
                            status: existing.status,
                        };
                    })
                    .sort((a, b) => a.organizationName.localeCompare(b.organizationName));
            }),
            catchError(() => {
                this.snackBar.open('Failed to load organizations', 'Close', { duration: 4000 });
                return of([]);
            })
        );
    }

    public saveVersion(id: string, version: string, organizationName: string): Observable<void> {
        return this.firestoreService
            .updateOrganization(id, { appVersion: version, appVersionChangedAt: new Date() })
            .pipe(
                tap(() =>
                    this.snackBar.open(`Version updated to ${version} for ${organizationName}`, 'Close', {
                        duration: 3000,
                    })
                ),
                catchError(() => {
                    this.snackBar.open(`Failed to update ${organizationName}`, 'Close', { duration: 3000 });
                    return throwError(() => new Error('save failed'));
                })
            );
    }

    public bumpVersion(id: string, newVersion: string, organizationName: string): Observable<void> {
        return this.firestoreService
            .updateOrganization(id, { appVersion: newVersion, appVersionChangedAt: new Date() })
            .pipe(
                tap(() =>
                    this.snackBar.open(
                        `Bumped ${organizationName} to ${newVersion} — deployment triggered`,
                        'Close',
                        { duration: 3000 }
                    )
                ),
                catchError(() => {
                    this.snackBar.open(`Failed to bump ${organizationName}`, 'Close', { duration: 3000 });
                    return throwError(() => new Error('bump failed'));
                })
            );
    }

    public bulkDeploy$(deployItems: { id: string; newVersion: string }[]): Observable<BulkDeployRowResult> {
        return from(deployItems).pipe(
            concatMap(({ id, newVersion }) =>
                this.firestoreService
                    .updateOrganization(id, { appVersion: newVersion, appVersionChangedAt: new Date() })
                    .pipe(
                        take(1),
                        map((): BulkDeployRowResult => ({ id, newVersion, outcome: 'success' })),
                        catchError((): Observable<BulkDeployRowResult> => of({ id, newVersion, outcome: 'error' }))
                    )
            )
        );
    }

    private toVersionRow(org: OrganizationData): VersionRow {
        const version = ensureVersion(org.appVersion);
        return {
            id: org.id!,
            organizationName: org.organizationName ?? 'Unknown',
            appVersion: version,
            appVersionChangedAt: toDate(org.appVersionChangedAt),
            editVersion: version,
            isEditVersionValid: true,
            status: 'idle',
        };
    }
}
